import { useEffect } from 'react';
import { JA, JA_PATTERNS } from './ja';

/**
 * Site-wide Japanese mode.
 *
 * Most of the site's copy is written directly in the page components, so instead of
 * threading a t() call through every component, this swaps rendered English text for
 * its Japanese entry in ./ja.js while JP is selected, and restores it for EN.
 * A MutationObserver keeps newly rendered text (route changes, dropdowns, charts)
 * translated. Text inside [data-no-translate] / [translate="no"] (e.g. articles
 * fetched from external sources) is left alone.
 */
const ATTRS = ['placeholder', 'aria-label', 'title', 'alt'];
const SKIP = 'script,style,noscript,code,pre,textarea,[data-no-translate],[translate="no"]';

function lookup(raw) {
  const key = raw.replace(/\s+/g, ' ').trim();
  if (!key || !/[A-Za-z]/.test(key)) return null;
  if (Object.prototype.hasOwnProperty.call(JA, key)) return JA[key];
  for (const [re, fn] of JA_PATTERNS) {
    const m = key.match(re);
    if (m) return fn(m);
  }
  if (process.env.NODE_ENV !== 'production') misses.add(key);
  return null;
}

const misses = new Set();
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  // Dev helper: run window.__i18nMisses() in the console to list untranslated text seen so far.
  window.__i18nMisses = () => [...misses].sort();
}

function translateValue(raw) {
  const ja = lookup(raw);
  if (ja == null) return null;
  const lead = raw.match(/^\s*/)[0];
  const trail = raw.match(/\s*$/)[0];
  return ja === '' ? '' : lead + ja + trail;
}

export default function DomTranslator({ lang }) {
  useEffect(() => {
    if (lang !== 'ja') return undefined;
    const root = document.body;
    const textOrig = new Map(); // text node -> { en, ja }
    const attrOrig = new Map(); // element -> { [attr]: { en, ja } }

    const doText = (node) => {
      const parent = node.parentElement;
      if (!parent || parent.closest(SKIP)) return;
      const cur = node.nodeValue;
      const rec = textOrig.get(node);
      if (rec && rec.ja === cur) return; // already ours
      const ja = translateValue(cur);
      if (ja != null && ja !== cur) {
        textOrig.set(node, { en: cur, ja });
        node.nodeValue = ja;
      } else if (rec) {
        textOrig.delete(node);
      }
    };

    const doAttrs = (el) => {
      if (el.closest(SKIP)) return;
      for (const a of ATTRS) {
        if (!el.hasAttribute(a)) continue;
        const cur = el.getAttribute(a);
        const recs = attrOrig.get(el) || {};
        if (recs[a] && recs[a].ja === cur) continue;
        const ja = translateValue(cur);
        if (ja != null && ja !== cur) {
          recs[a] = { en: cur, ja };
          attrOrig.set(el, recs);
          el.setAttribute(a, ja);
        }
      }
    };

    const walk = (start) => {
      if (start.nodeType === Node.TEXT_NODE) return doText(start);
      if (start.nodeType !== Node.ELEMENT_NODE) return undefined;
      doAttrs(start);
      const tw = document.createTreeWalker(start, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
      let n = tw.nextNode();
      while (n) {
        if (n.nodeType === Node.TEXT_NODE) doText(n);
        else doAttrs(n);
        n = tw.nextNode();
      }
      return undefined;
    };

    walk(root);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'characterData') doText(m.target);
        else if (m.type === 'attributes') doAttrs(m.target);
        else m.addedNodes.forEach(walk);
      }
    });
    observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });

    return () => {
      observer.disconnect();
      textOrig.forEach(({ en, ja }, node) => {
        if (node.nodeValue === ja) node.nodeValue = en;
      });
      attrOrig.forEach((recs, el) => {
        Object.entries(recs).forEach(([a, { en, ja }]) => {
          if (el.getAttribute(a) === ja) el.setAttribute(a, en);
        });
      });
    };
  }, [lang]);

  return null;
}
